import * as React from 'react';
import { cn } from '@se-platform/ui/utils';
import { ScienceExchangeMark } from './way-in-glyphs';

// Where the one decorative gradient pill sits on a screen. Each screen of the
// treatment places it differently so consecutive screens do not look identical.
export type ShellPillPosition = 'top-right' | 'mid-right' | 'bottom-right' | 'top-left';

const PILL_POSITION_CLASSES: Record<ShellPillPosition, string> = {
  'top-right': '-right-40 -top-48',
  'mid-right': '-right-44 top-72',
  'bottom-right': '-bottom-56 -right-44',
  'top-left': '-left-40 -top-48',
};

interface WayInShellProps {
  // Margin content: the numeral or word mark, step label, progress and orienting note.
  rail: React.ReactNode;
  // The question and its controls.
  children: React.ReactNode;
  // The action bar that closes every screen: primary button, secondary link, right-aligned aside.
  actions: React.ReactNode;
  // Right-hand end of the top bar. Defaults to the "Supplier onboarding" eyebrow.
  topBarEnd?: React.ReactNode;
  // Optional context shown after a divider beside the wordmark, e.g. the supplier's company name once known.
  topBarContext?: string;
  pill?: ShellPillPosition;
  // The warm gradient alternates with the cool one screen to screen.
  pillTone?: 'cool' | 'warm';
}

/**
 * The frame every "way in" screen shares: a hairline top bar with the wordmark, a
 * 240px left margin that carries orientation, and a 760px content column that
 * carries nothing but the question. One rotated gradient pill per screen is the
 * only decoration. Below the frame width the margin stacks above the column.
 */
export function WayInShell({
  rail,
  children,
  actions,
  topBarEnd,
  topBarContext,
  pill = 'top-right',
  pillTone = 'cool',
}: WayInShellProps) {
  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-brand-paper font-body text-brand-ink">
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute h-pill-tall w-pill rotate-45 rounded-full opacity-10',
          pillTone === 'warm' ? 'bg-gradient-brand-warm' : 'bg-gradient-brand',
          PILL_POSITION_CLASSES[pill],
        )}
      />

      <header className="relative border-b border-brand-line">
        <div className="mx-auto flex w-full max-w-frame flex-wrap items-center gap-3 px-6 py-[15px] lg:px-gutter">
          <div className="flex items-center gap-1">
            <ScienceExchangeMark />
            <div className="pt-1 font-display text-brand-wordmark text-brand-black">
              <span className="font-light">Science</span>
              <span className="font-bold">Exchange</span>
            </div>
          </div>
          {topBarContext ? (
            <>
              <div
                aria-hidden="true"
                className="mx-1 hidden h-[18px] w-px bg-brand-line sm:block"
              />
              <div className="hidden text-brand-fine text-brand-muted sm:block">
                {topBarContext}
              </div>
            </>
          ) : null}
          <div className="flex-grow" />
          {topBarEnd ?? (
            <div className="font-display text-brand-eyebrow uppercase text-brand-muted">
              Supplier onboarding
            </div>
          )}
        </div>
      </header>

      <div className="relative mx-auto flex w-full max-w-frame flex-grow flex-col px-6 lg:flex-row lg:px-gutter">
        <aside className="w-full shrink-0 pt-10 lg:w-rail lg:pt-[62px]">{rail}</aside>

        <main className="flex min-w-0 flex-grow flex-col pb-8 pt-10 lg:pl-rail-gap lg:pt-[62px]">
          <div className="flex w-full max-w-column flex-grow flex-col">
            <div className="mb-auto">{children}</div>
            <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-brand-line pt-5">
              {actions}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Right-aligned line in the action bar that prices the screen ("About 2 minutes")
 * or states what has not happened yet ("Nobody has been emailed yet").
 */
export function ActionAside({ children }: { children: React.ReactNode }) {
  return <span className="ml-auto text-brand-fine text-brand-muted">{children}</span>;
}

/**
 * The top-bar pill the in-portal task screens carry instead of the onboarding
 * eyebrow: a gradient dot and a short status ("First task", "One click left").
 */
export function TaskPill({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-brand-ground py-1.5 pl-[7px] pr-3.5 text-brand-meta text-brand-ink">
      <span aria-hidden="true" className="block h-5 w-5 rounded-full bg-gradient-brand" />
      {children}
    </div>
  );
}
